import { Fragment, memo } from "react";
import { makeStyles } from "tss-react/mui";
import { MiButton } from "@miplatform/components";
import { BUTTON_COLORS, BUTTON_VARIANTS } from "@miplatform/constants";
import { mdiAlertOutline } from "@mdi/js";
import { Box, Tooltip } from "@mui/material";

/**
 * OrderAlerts Component
 *
 * This memoized component renders a warning-styled alert button if alerts are present.
 * When hovered over, the button displays a tooltip with one or more alert messages.
 *
 * Alerts can be a single string or an array of strings. If it's an array, each alert
 * is separated by a line break in the tooltip.
 *
 * @component
 * @example
 * // Single alert
 * <OrderAlerts alerts="This is a single warning" />
 *
 * @example
 * // Multiple alerts
 * <OrderAlerts alerts={["Alert 1", "Alert 2"]} />
 *
 * @param {Object} props
 * @param {string|string[]} props.alerts - One or more alert messages to display. If falsy, no button or tooltip is rendered.
 * @param {error | warning} props.color - The color of the button. This prop is not used in the current implementation but can be extended for future use.
 *
 * @returns {JSX.Element|null} Returns the alert button with a tooltip if alerts are provided, otherwise returns null.
 */
export const OrderAlerts = memo(({ alerts, color = BUTTON_COLORS.ORANGE }) => {
  const { classes, theme } = useStyles({ color });

  return alerts ? (
    <Tooltip
      title={
        <>
          {(Array.isArray(alerts) ? alerts : [alerts]).map((alert, i) => (
            <Fragment key={alert}>
              {alert}
              {i !== alerts.length - 1 && <br />}
            </Fragment>
          ))}
        </>
      }
      slotProps={{
        popper: { placement: "right", sx: { p: 0 } },
        tooltip: {
          sx: {
            backgroundColor: theme.palette.warning.dark,
            fontSize: theme.typography.pxToRem(14),
          },
        },
      }}
    >
      <Box>
        <MiButton
          size="small"
          className={classes.alertButton}
          variant={BUTTON_VARIANTS.CONTAINED}
          color={color}
          startIcon={{ path: mdiAlertOutline, iconSize: 0.5 }}
        />
      </Box>
    </Tooltip>
  ) : null;
});

const useStyles = makeStyles()((theme, { color }) => ({
  alertButton: {
    backgroundColor: theme.palette[color].dark,
    padding: theme.spacing(0.3, 1.5),
    marginLeft: theme.spacing(1),
  },
}));
